import { useEffect, useRef, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api";
import { BACKEND_URL, supabase } from "@/lib/supabase";
import { FetchEventSource } from "@/lib/sse";
import { announce } from "@/lib/announce";
import { aiNameToPersonaDisplay } from "@/lib/ai-name-mapping";
import { computeDurationMinutes } from "@/lib/verdict-format";
import type { VerdictClarity } from "@/components/Verdict/VerdictClarity";
import type {
  Branch,
  TendancesClassification,
  TendancesInstrument,
  TendancesReco,
} from "@/lib/branches";

export type DissidencePersona =
  | "delphine"
  | "henri"
  | "akilah"
  | "eva"
  | "ren"
  | "julien"
  | "imani";

export interface VerdictCondition {
  text: string;
}

export interface VerdictDissidence {
  persona: DissidencePersona | null;
  name: string;
  role: string;
  model: string;
  text: string;
}

/** Une intervention d'un conseiller dans un acte. */
export interface ActeBubble {
  /** speaker brut renvoyé par le backend (ex: "Claude", "GPT-4", "DeepSeek"). */
  speaker: string;
  text: string;
  /** Acte II : indique que cette intervention conteste une précédente. */
  isContestation?: boolean;
  /** Optionnel — timestamp de l'intervention. */
  time?: string;
}

/** Une contradiction documentée par le Greffier. */
export interface ProofLayerEntry {
  ai: string;
  claim: string;
  correction: string;
  correctedBy: string;
}

export interface SourceLink {
  title: string;
  url: string;
  /** Origine pour favicon (déduite de l'url si absente). */
  domain?: string;
}

export interface VerdictData {
  id: string;
  conversationId: string;
  auditAxis: string | null;
  saisineNumber: string;
  saisineDate: string;
  saisineTime: string;
  durationMinutes: number;
  mode: string;
  question: string;
  headline: string;
  subHeadline: string;
  /** Prose complète (~700 mots) du verdict. */
  verdictText: string;
  conditions: VerdictCondition[];
  confidenceScore: number;
  confidenceLabel: string;
  dissidence?: VerdictDissidence;
  /** Synthèse / dernier mot de l'Acte III si fourni en plat. */
  consensusSummary?: string;
  acts: {
    act1: ActeBubble[];
    act2: ActeBubble[];
    act3: ActeBubble[];
  };
  proofLayer: ProofLayerEntry[];
  researchSources: SourceLink[];
  sources: SourceLink[];
  researchSourcesFull: {
    total: number;
    by_category: Record<string, number>;
    items: Record<string, Array<{
      url: string;
      title?: string;
      snippet?: string;
      source_name?: string;
      thumbnail?: string;
      discovered_at?: string;
    }>>;
  } | null;
  verdictClarity: VerdictClarity | null;
  verdictClarityGeneratedAt: string | null;
  verdictAuditDiagnostic: VerdictAuditDiagnostic | null;
  greffierActsSummary: {
    act1: string | null;
    act2: string | null;
    act3: string | null;
  };
  procesVerbal: {
    acte1: string | null;
    acte2: string | null;
    acte3: string | null;
  } | null;
  branch: Branch | null;
  tendancesInstrument: TendancesInstrument | null;
  verifObservations: VerifObservationsData | null;
}

// ─── Vérif : observations à 3 temps ───
export type VerifConvergence = "faible" | "moyen" | "fort";
export type VerifPro =
  | "avocat"
  | "expert_comptable"
  | "notaire"
  | "conseil_pi"
  | "banquier"
  | "autre";

export interface VerifObservation {
  constat: string;
  ancrage: string;
  droit_general: string;
  angle: string;
  convergence: VerifConvergence;
  question: string;
  pro_a_consulter: VerifPro;
}

export interface VerifObservationsData {
  rappel_faits?: string;
  observations: VerifObservation[];
  angles_non_couverts: string[];
}


/** Diagnostic d'audit (branche audit / mode positionnement). */
export type AuditDimensionStatus = "vert" | "orange" | "rouge" | "inconnu";

export interface AuditDimension {
  key: string;
  label: string;
  status: AuditDimensionStatus;
  constat: string;
}

export interface AuditPlanItem {
  titre: string;
  detail?: string;
  effort?: string;
  delai?: string;
}

export interface VerdictAuditDiagnostic {
  axe_prioritaire: { question: string; reponse: string } | null;
  /** Backend renvoie reponse_axe = { titre, axe (slug), reponse }. */
  reponse_axe: { titre: string; axe: string; reponse: string } | null;
  score_global: { value: number; scale: number } | null;
  dimensions: AuditDimension[];
  plan_correctif: {
    quick_wins: AuditPlanItem[];
    chantiers_fond: AuditPlanItem[];
  };
  angles_morts: Array<{ titre: string; explication?: string }>;
  signal_visibilite: { present: boolean; message: string } | null;
}

function normalizeProcesVerbal(raw: unknown): VerdictData["procesVerbal"] {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as BackendVerdict;
  const acte1 = cleanSummary(pick(o, "paragraphe_acte_1", "paragrapheActe1", "acte_1", "acte1"));
  const acte2 = cleanSummary(pick(o, "paragraphe_acte_2", "paragrapheActe2", "acte_2", "acte2"));
  const acte3 = cleanSummary(pick(o, "paragraphe_acte_3", "paragrapheActe3", "acte_3", "acte3"));
  if (!acte1 && !acte2 && !acte3) return null;
  return { acte1, acte2, acte3 };
}

const MINI_MODE_RE = /(non applicable\s*[—\-–]?\s*mini-?mode|acte\s+[ivx]+\s+non applicable|synth[èe]se\s+non disponible)/i;


function cleanSummary(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!t) return null;
  if (MINI_MODE_RE.test(t)) return null;
  return t;
}

function normalizeGreffierActsSummary(raw: unknown): VerdictData["greffierActsSummary"] {
  const empty = { act1: null, act2: null, act3: null };
  if (!raw || typeof raw !== "object") return empty;
  const o = raw as BackendVerdict;
  const summary = (pick(o, "acts_summary", "actsSummary") ?? {}) as BackendVerdict;
  return {
    act1: cleanSummary(pick(summary, "act_1", "act1")),
    act2: cleanSummary(pick(summary, "act_2", "act2")),
    act3: cleanSummary(pick(summary, "act_3", "act3")),
  };
}

// Accept backend (snake_case) or already-normalized (camelCase) shapes.
type BackendVerdict = Record<string, unknown>;

function pick<T = unknown>(obj: BackendVerdict, ...keys: string[]): T | undefined {
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null) return v as T;
  }
  return undefined;
}

function normalizeConditions(raw: unknown): VerdictCondition[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((c) => {
      if (typeof c === "string") return { text: c };
      if (c && typeof c === "object") {
        const text = (c as Record<string, unknown>).text;
        if (typeof text === "string") return { text };
      }
      return null;
    })
    .filter((x): x is VerdictCondition => x !== null);
}

function normalizeDissidence(raw: unknown): VerdictDissidence | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const d = raw as BackendVerdict;
  const text = pick<string>(d, "text", "body", "content");
  if (!text) return undefined;
  const aiName = pick<string>(d, "aiName", "ai_name", "model");
  const personaRaw = pick<string>(d, "persona");
  const role = pick<string>(d, "role") ?? "";
  const explicitModel = pick<string>(d, "model");

  if (aiName) {
    const disp = aiNameToPersonaDisplay(aiName);
    return {
      persona: disp.persona,
      name: disp.name,
      role,
      model: disp.modelLabel || explicitModel || "",
      text,
    };
  }
  if (personaRaw) {
    return {
      persona: personaRaw as DissidencePersona,
      name: personaRaw.charAt(0).toUpperCase() + personaRaw.slice(1),
      role,
      model: explicitModel ?? "",
      text,
    };
  }
  return {
    persona: null,
    name: "Conseil",
    role,
    model: explicitModel ?? "",
    text,
  };
}

function normalizeActeBubble(raw: unknown): ActeBubble | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as BackendVerdict;
  const speaker =
    pick<string>(o, "speaker", "aiName", "ai_name", "model", "persona") ?? "";
  const text = pick<string>(o, "text", "body", "content", "message") ?? "";
  if (!text) return null;
  // Filtre les placeholders backend (ex: "ACTE II non applicable — mini-mode").
  if (MINI_MODE_RE.test(text)) return null;
  const isContestation = Boolean(
    pick(o, "is_contestation", "isContestation", "contestation"),
  );
  const time = pick<string>(o, "time", "timestamp", "at");
  return { speaker, text, isContestation, time };
}

function normalizeActe(raw: unknown): ActeBubble[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeActeBubble)
      .filter((x): x is ActeBubble => x !== null);
  }
  // act_3 peut arriver en {text: "synthèse"} → on l'expose en bulle Conseil.
  if (raw && typeof raw === "object") {
    const o = raw as BackendVerdict;
    if (Array.isArray(o.bubbles)) {
      return (o.bubbles as unknown[])
        .map(normalizeActeBubble)
        .filter((x): x is ActeBubble => x !== null);
    }
    const text = pick<string>(o, "text", "summary", "synthesis");
    if (text && !MINI_MODE_RE.test(text)) return [{ speaker: "", text }];
  }
  return [];
}

function normalizeActs(raw: unknown): VerdictData["acts"] {
  const empty = { act1: [], act2: [], act3: [] };
  if (!raw || typeof raw !== "object") return empty;
  const a = raw as BackendVerdict;
  return {
    act1: normalizeActe(pick(a, "act_1", "act1", "acte_1", "acte1")),
    act2: normalizeActe(pick(a, "act_2", "act2", "acte_2", "acte2")),
    act3: normalizeActe(pick(a, "act_3", "act3", "acte_3", "acte3")),
  };
}

function normalizeProofLayer(raw: unknown): ProofLayerEntry[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((e) => {
      if (!e || typeof e !== "object") return null;
      const o = e as BackendVerdict;
      const ai = pick<string>(o, "ai", "aiName", "ai_name", "speaker") ?? "";
      const claim = pick<string>(o, "claim", "statement", "original") ?? "";
      const correction =
        pick<string>(o, "correction", "fix", "corrected") ?? "";
      const correctedBy =
        pick<string>(o, "corrected_by", "correctedBy", "corrector") ?? "";
      if (!claim && !correction) return null;
      return { ai, claim, correction, correctedBy };
    })
    .filter((x): x is ProofLayerEntry => x !== null);
}

function normalizeSources(raw: unknown): SourceLink[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((s): SourceLink | null => {
      if (typeof s === "string") {
        try {
          const u = new URL(s);
          return { title: u.hostname, url: s, domain: u.hostname };
        } catch {
          return { title: s, url: s };
        }
      }
      if (!s || typeof s !== "object") return null;
      const o = s as BackendVerdict;
      const url = pick<string>(o, "url", "href", "link");
      if (!url) return null;
      const title = pick<string>(o, "title", "label", "name") ?? url;
      let domain = pick<string>(o, "domain");
      if (!domain) {
        try {
          domain = new URL(url).hostname;
        } catch {
          /* noop */
        }
      }
      return { title, url, domain };
    })
    .filter((x): x is SourceLink => x !== null);
}

function normalizeBranch(raw: unknown): Branch | null {
  if (raw === "business" || raw === "tendances" || raw === "audit" || raw === "verif") return raw;
  return null;
}

const TENDANCES_CLASSIFICATIONS: TendancesClassification[] = [
  "Micro",
  "Saisonnière",
  "Macro",
  "Méga",
];
const TENDANCES_RECOS: TendancesReco[] = ["Acheter", "Surveiller", "Ignorer"];

function normalizeTendancesInstrument(
  clarity: unknown,
): TendancesInstrument | null {
  if (!clarity || typeof clarity !== "object") return null;
  const instrument = (clarity as Record<string, unknown>).instrument;
  if (!instrument || typeof instrument !== "object") return null;
  const i = instrument as Record<string, unknown>;
  if (i.kind !== "tendances") return null;
  const classification = i.classification as TendancesClassification;
  const reco = i.reco as TendancesReco;
  const justification =
    typeof i.justification === "string" ? i.justification : "";
  if (!TENDANCES_CLASSIFICATIONS.includes(classification)) return null;
  if (!TENDANCES_RECOS.includes(reco)) return null;
  return { kind: "tendances", classification, reco, justification };
}

/**
 * Lit la clarity quel que soit l'endpoint :
 * - GET /api/verdict/:id        → raw.verdict_clarity (snake, racine)
 * - GET /api/chat/:id/history   → raw.conversation.verdictClarityJson (camel, imbriqué)
 */
function readClarity(raw: BackendVerdict | null | undefined): VerdictClarity | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const direct =
    r.verdict_clarity ??
    r.verdictClarity ??
    r.verdict_clarity_json ??
    r.verdictClarityJson;
  if (direct && typeof direct === "object") return direct as VerdictClarity;
  const conv = r.conversation as Record<string, unknown> | undefined;
  if (conv && typeof conv === "object") {
    const nested =
      conv.verdictClarityJson ??
      conv.verdict_clarity_json ??
      conv.verdictClarity ??
      conv.verdict_clarity;
    if (nested && typeof nested === "object") return nested as VerdictClarity;
  }
  return null;
}

const VERIF_CONVERGENCE: VerifConvergence[] = ["faible", "moyen", "fort"];
const VERIF_PROS: VerifPro[] = [
  "avocat",
  "expert_comptable",
  "notaire",
  "conseil_pi",
  "banquier",
  "autre",
];

function normalizeVerifObservations(
  clarity: unknown,
): VerifObservationsData | null {
  if (!clarity || typeof clarity !== "object") return null;
  const c = clarity as Record<string, unknown>;
  if (c.kind !== "verif_observations") return null;
  const rawObs = Array.isArray(c.observations) ? c.observations : [];
  const observations: VerifObservation[] = rawObs
    .map((o): VerifObservation | null => {
      if (!o || typeof o !== "object") return null;
      const r = o as Record<string, unknown>;
      const constat = typeof r.constat === "string" ? r.constat : "";
      const question = typeof r.question === "string" ? r.question : "";
      if (!constat && !question) return null;
      const convRaw = typeof r.convergence === "string" ? r.convergence : "moyen";
      const convergence: VerifConvergence = VERIF_CONVERGENCE.includes(
        convRaw as VerifConvergence,
      )
        ? (convRaw as VerifConvergence)
        : "moyen";
      const proRaw = typeof r.pro_a_consulter === "string" ? r.pro_a_consulter : "autre";
      const pro_a_consulter: VerifPro = VERIF_PROS.includes(proRaw as VerifPro)
        ? (proRaw as VerifPro)
        : "autre";
      return {
        constat,
        ancrage: typeof r.ancrage === "string" ? r.ancrage : "",
        droit_general: typeof r.droit_general === "string" ? r.droit_general : "",
        angle: typeof r.angle === "string" ? r.angle : "",
        convergence,
        question,
        pro_a_consulter,
      };
    })
    .filter((x): x is VerifObservation => x !== null);
  const anglesRaw = Array.isArray(c.angles_non_couverts) ? c.angles_non_couverts : [];
  const angles_non_couverts = anglesRaw.filter(
    (x): x is string => typeof x === "string" && x.trim().length > 0,
  );
  const rappelRaw = typeof c.rappel_faits === "string" ? c.rappel_faits.trim() : "";
  const rappel_faits = rappelRaw.length > 0 ? rappelRaw : undefined;
  return { rappel_faits, observations, angles_non_couverts };
}




const DIM_KEYS: Array<{ key: string; label: string }> = [
  { key: "perception_marche", label: "Perception marché" },
  { key: "concurrence", label: "Concurrence" },
  { key: "clarte_message", label: "Clarté du message" },
  { key: "differenciation", label: "Différenciation" },
  { key: "part_de_voix", label: "Part de voix" },
];

function normStatus(v: unknown): AuditDimensionStatus {
  const s = typeof v === "string" ? v.toLowerCase().trim() : "";
  if (!s || s === "inconnu" || s === "unknown" || s === "gris" || s === "grey" || s === "gray") return "inconnu";
  if (s === "vert" || s === "green" || s === "ok") return "vert";
  if (s === "jaune" || s === "orange" || s === "yellow" || s === "warn") return "orange";
  if (s === "rouge" || s === "red" || s === "ko" || s === "critical") return "rouge";
  return "inconnu";
}

function normalizeDimensions(raw: unknown): AuditDimension[] {
  const pickStatus = (o: Record<string, unknown>) =>
    normStatus(o.feu ?? o.status ?? o.color ?? o.couleur);
  const pickConstat = (o: Record<string, unknown>) =>
    typeof o.constat === "string"
      ? o.constat
      : typeof o.commentaire === "string"
        ? o.commentaire
        : typeof o.summary === "string"
          ? o.summary
          : "";
  const found = new Map<string, AuditDimension>();
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const o = item as Record<string, unknown>;
      const key = typeof o.key === "string" ? o.key : typeof o.id === "string" ? o.id : "";
      const known = DIM_KEYS.find((d) => d.key === key);
      const label =
        typeof o.label === "string" && o.label.trim() ? o.label : known?.label ?? key;
      found.set(key, { key, label, status: pickStatus(o), constat: pickConstat(o) });
    }
  } else if (raw && typeof raw === "object") {
    const r = raw as Record<string, unknown>;
    for (const { key, label } of DIM_KEYS) {
      const v = r[key];
      if (!v || typeof v !== "object") continue;
      const o = v as Record<string, unknown>;
      found.set(key, { key, label, status: pickStatus(o), constat: pickConstat(o) });
    }
  }
  // Toujours rendre les 5 dimensions canoniques dans le bon ordre.
  return DIM_KEYS.map(
    ({ key, label }) =>
      found.get(key) ?? { key, label, status: "inconnu" as AuditDimensionStatus, constat: "" },
  );
}

function normalizePlanItems(raw: unknown): AuditPlanItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      if (typeof item === "string") return { titre: item };
      if (!item || typeof item !== "object") return null;
      const o = item as Record<string, unknown>;
      const titre =
        (typeof o.titre === "string" && o.titre) ||
        (typeof o.title === "string" && o.title) ||
        (typeof o.action === "string" && o.action) ||
        "";
      if (!titre) return null;
      return {
        titre,
        detail: typeof o.detail === "string" ? o.detail : (typeof o.description === "string" ? o.description : undefined),
        effort: typeof o.effort === "string" ? o.effort : (typeof o.cout === "string" ? o.cout : undefined),
        delai: typeof o.delai === "string" ? o.delai : (typeof o.temps === "string" ? o.temps : undefined),
      } as AuditPlanItem;
    })
    .filter((x): x is AuditPlanItem => x !== null);
}

function readAuditDiagnostic(raw: BackendVerdict | null | undefined): VerdictAuditDiagnostic | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const direct =
    r.verdict_audit_diagnostic ??
    r.verdictAuditDiagnostic ??
    r.audit_diagnostic ??
    r.auditDiagnostic;
  let d: Record<string, unknown> | null =
    direct && typeof direct === "object" ? (direct as Record<string, unknown>) : null;
  if (!d) {
    const conv = r.conversation as Record<string, unknown> | undefined;
    if (conv && typeof conv === "object") {
      const nested =
        conv.verdictAuditDiagnostic ??
        conv.verdict_audit_diagnostic ??
        conv.auditDiagnostic ??
        conv.audit_diagnostic;
      if (nested && typeof nested === "object") d = nested as Record<string, unknown>;
    }
  }
  if (!d) return null;

  const axe = (d.axe_prioritaire ?? d.axePrioritaire) as Record<string, unknown> | undefined;
  const axePrior =
    axe && typeof axe === "object"
      ? {
          question: typeof axe.question === "string" ? axe.question : (typeof axe.q === "string" ? axe.q : ""),
          reponse:
            typeof axe.reponse === "string"
              ? axe.reponse
              : typeof axe.response === "string"
                ? axe.response
                : typeof axe.answer === "string"
                  ? axe.answer
                  : "",
        }
      : null;

  const repAxeRaw = (d.reponse_axe ?? d.reponseAxe) as Record<string, unknown> | undefined;
  const reponseAxe =
    repAxeRaw && typeof repAxeRaw === "object"
      ? {
          titre:
            typeof repAxeRaw.titre === "string"
              ? repAxeRaw.titre
              : typeof repAxeRaw.title === "string"
                ? repAxeRaw.title
                : typeof repAxeRaw.label === "string"
                  ? repAxeRaw.label
                  : "",
          axe:
            typeof repAxeRaw.axe === "string"
              ? repAxeRaw.axe
              : typeof repAxeRaw.slug === "string"
                ? repAxeRaw.slug
                : typeof repAxeRaw.key === "string"
                  ? repAxeRaw.key
                  : "",
          reponse:
            typeof repAxeRaw.reponse === "string"
              ? repAxeRaw.reponse
              : typeof repAxeRaw.response === "string"
                ? repAxeRaw.response
                : "",
        }
      : null;

  const scoreRaw = (d.score_global ?? d.scoreGlobal ?? d.score) as unknown;
  let scoreGlobal: { value: number; scale: number } | null = null;
  if (typeof scoreRaw === "number") {
    scoreGlobal = { value: scoreRaw, scale: 10 };
  } else if (scoreRaw && typeof scoreRaw === "object") {
    const so = scoreRaw as Record<string, unknown>;
    const v = typeof so.value === "number" ? so.value : null;
    const sc = typeof so.scale === "number" ? so.scale : 10;
    if (v !== null) scoreGlobal = { value: v, scale: sc };
  }

  const dims = normalizeDimensions(d.dimensions);

  const plan = (d.plan_correctif ?? d.planCorrectif) as Record<string, unknown> | undefined;
  const planCorrectif = {
    quick_wins: normalizePlanItems(plan?.quick_wins ?? plan?.quickWins),
    chantiers_fond: normalizePlanItems(plan?.chantiers_fond ?? plan?.chantiersFond ?? plan?.chantiers),
  };

  const anglesRaw = (d.angles_morts ?? d.anglesMorts ?? []) as unknown;
  const anglesMorts: Array<{ titre: string; explication?: string }> = Array.isArray(anglesRaw)
    ? anglesRaw
        .map((a) => {
          if (typeof a === "string") return { titre: a };
          if (!a || typeof a !== "object") return null;
          const o = a as Record<string, unknown>;
          const titre =
            (typeof o.titre === "string" && o.titre) ||
            (typeof o.title === "string" && o.title) ||
            "";
          if (!titre) return null;
          return {
            titre,
            explication:
              typeof o.explication === "string"
                ? o.explication
                : typeof o.detail === "string"
                  ? o.detail
                  : undefined,
          };
        })
        .filter((x): x is { titre: string; explication?: string } => x !== null)
    : [];

  const sigRaw = (d.signal_visibilite ?? d.signalVisibilite) as unknown;
  let signal: { present: boolean; message: string } | null = null;
  if (typeof sigRaw === "string" && sigRaw.trim()) {
    signal = { present: true, message: sigRaw.trim() };
  } else if (sigRaw && typeof sigRaw === "object") {
    const so = sigRaw as Record<string, unknown>;
    const msg =
      (typeof so.message === "string" && so.message) ||
      (typeof so.text === "string" && so.text) ||
      "";
    if (msg) signal = { present: so.present !== false, message: msg };
  }

  return {
    axe_prioritaire: axePrior,
    reponse_axe: reponseAxe,
    score_global: scoreGlobal,
    dimensions: dims,
    plan_correctif: planCorrectif,
    angles_morts: anglesMorts,
    signal_visibilite: signal,
  };
}

export function normalizeVerdict(id: string, raw: BackendVerdict): VerdictData {
  const explicitDuration = pick<number>(raw, "duration_minutes", "durationMinutes");
  const computed = computeDurationMinutes(
    pick<string>(raw, "created_at", "createdAt"),
    pick<string>(raw, "finished_at", "finishedAt"),
  );
  const acts = normalizeActs(pick(raw, "acts"));
  const clarity = readClarity(raw);
  // Si act_3 contient une unique bulle "Conseil" (text plat), c'est aussi la synthèse.
  const rawVerdictText =
    pick<string>(raw, "verdict_text", "verdictText", "verdict") ?? "";
  const rawConsensus =
    pick<string>(raw, "consensus_summary", "consensusSummary") ??
    (acts.act3.length === 1 && !acts.act3[0].speaker
      ? acts.act3[0].text
      : undefined);
  // Dédoublonnage : si consensus_summary == verdict_text (ou inclus dedans),
  // on n'affiche pas la section II pour éviter le doublon visible.
  const normalize = (s: string) =>
    s.replace(/\s+/g, " ").trim().toLowerCase();
  const consensusSummary =
    rawConsensus &&
    rawVerdictText &&
    (normalize(rawConsensus) === normalize(rawVerdictText) ||
      normalize(rawVerdictText).includes(normalize(rawConsensus)))
      ? undefined
      : rawConsensus;

  return {
    id: pick<string>(raw, "id") ?? id,
    conversationId:
      pick<string>(raw, "conversation_id", "conversationId") ??
      pick<string>(raw, "id") ??
      id,
    auditAxis: (() => {
      const v =
        pick<string>(raw, "audit_axis", "auditAxis") ??
        (() => {
          const conv = (raw as Record<string, unknown>).conversation as
            | Record<string, unknown>
            | undefined;
          const cv = conv && (conv.audit_axis ?? conv.auditAxis);
          return typeof cv === "string" ? cv : undefined;
        })();
      return typeof v === "string" && v.trim() ? v.trim() : null;
    })(),
    saisineNumber: pick<string>(raw, "saisine_number", "saisineNumber") ?? "",
    saisineDate: pick<string>(raw, "saisine_date", "saisineDate") ?? "",
    saisineTime: pick<string>(raw, "saisine_time", "saisineTime") ?? "",
    durationMinutes: explicitDuration ?? computed,
    mode: pick<string>(raw, "mode") ?? "",
    question: pick<string>(raw, "question") ?? "",
    headline: pick<string>(raw, "headline") ?? "",
    subHeadline: pick<string>(raw, "sub_headline", "subHeadline") ?? "",
    verdictText: rawVerdictText,
    conditions: normalizeConditions(pick(raw, "conditions")),
    confidenceScore:
      pick<number>(
        raw,
        "confidence_score",
        "confidenceScore",
        "conviction_index",
        "convictionIndex",
        "verdict_score",
        "verdictScore",
      ) ?? 0,
    confidenceLabel:
      pick<string>(raw, "confidence_label", "confidenceLabel") ?? "",
    dissidence: normalizeDissidence(pick(raw, "dissidence", "dissenting_note", "dissentingNote")),
    consensusSummary,
    acts,
    proofLayer: normalizeProofLayer(
      pick(raw, "proof_layer", "proofLayer", "contradictions"),
    ),
    researchSources: normalizeSources(
      pick(raw, "research_sources", "researchSources"),
    ),
    sources: normalizeSources(pick(raw, "sources")),
    researchSourcesFull: (() => {
      const rsf = pick(raw, "research_sources_full", "researchSourcesFull") as
        | VerdictData["researchSourcesFull"]
        | null
        | undefined;
      if (!rsf || typeof rsf !== "object") return null;
      const total = typeof rsf.total === "number" ? rsf.total : 0;
      const by_category = (rsf.by_category && typeof rsf.by_category === "object") ? rsf.by_category : {};
      const items = (rsf.items && typeof rsf.items === "object") ? rsf.items : {};
      return { total, by_category, items };
    })(),
    verdictClarity: clarity,
    verdictClarityGeneratedAt:
      pick<string>(raw, "verdict_clarity_generated_at", "verdictClarityGeneratedAt") ?? null,
    greffierActsSummary: normalizeGreffierActsSummary(
      pick(raw, "greffier_briefing", "greffierBriefing"),
    ),
    procesVerbal: normalizeProcesVerbal(
      pick(raw, "proces_verbal", "procesVerbal"),
    ),
    branch: normalizeBranch(pick<string>(raw, "branch")),
    tendancesInstrument: normalizeTendancesInstrument(clarity),
    verdictAuditDiagnostic: readAuditDiagnostic(raw),
    verifObservations: normalizeVerifObservations(clarity),
  };
}


export function useVerdict(id: string): {
  data: VerdictData | null;
  isLoading: boolean;
  error: Error | null;
  clarityPending: boolean;
  clarityFailed: boolean;
} {
  const [data, setData] = useState<VerdictData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isPolling, setIsPolling] = useState<boolean>(true);
  const [clarityFailed, setClarityFailed] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let es: FetchEventSource | null = null;
    let pollStartedAt = 0;
    let nextDelay = 3000;
    const POLL_MAX_MS = 10 * 60_000;
    const POLL_DELAY_CAP_MS = 30_000;

    setIsLoading(true);
    setError(null);
    setIsPolling(true);
    setClarityFailed(false);

    const scheduleNext = (immediate = false) => {
      if (cancelled) return;
      if (timer) clearTimeout(timer);
      if (Date.now() - pollStartedAt >= POLL_MAX_MS) {
        setIsPolling(false);
        setClarityFailed(true);
        return;
      }
      const delay = immediate ? 0 : nextDelay;
      timer = setTimeout(load, delay);
      nextDelay = Math.min(Math.round(nextDelay * 1.6), POLL_DELAY_CAP_MS);
    };

    const load = () => {
      apiFetch<BackendVerdict>(`/api/verdict/${id}`)
        .then((raw) => {
          if (cancelled) return;
          const next = normalizeVerdict(id, raw);
          setData(next);
          setIsLoading(false);
          const isAudit = next.branch === "audit";
          const isVerif = next.branch === "verif";
          const needsAudit = isAudit && !next.verdictAuditDiagnostic;
          const needsVerif = isVerif && !next.verifObservations;
          const needsClarity = !isAudit && !isVerif && (!next.verdictClarity || !next.procesVerbal);
          if (needsAudit || needsVerif || needsClarity) {
            setIsPolling(true);
            scheduleNext(false);
          } else {
            setIsPolling(false);
            setClarityFailed(false);
            if (timer) clearTimeout(timer);
            es?.close();
          }
        })
        .catch((err) => {
          if (cancelled) return;
          const message =
            err instanceof ApiError
              ? err.status === 404
                ? "Verdict introuvable"
                : err.message
              : err instanceof Error
                ? err.message
                : "Erreur inconnue";
          setError(new Error(message));
          setData(null);
          setIsLoading(false);
          setIsPolling(false);
        });
    };

    pollStartedAt = Date.now();
    load();

    // SSE : sur clarity_ready / complete, re-fetch immédiat pour court-circuiter
    // le polling. On ouvre le flux après un premier load pour connaître la conv.
    (async () => {
      try {
        const raw = await apiFetch<BackendVerdict>(`/api/verdict/${id}`);
        if (cancelled) return;
        const conversationId =
          (pick<string>(raw, "conversation_id", "conversationId") ?? id);
        const { data: sessData } = await supabase.auth.getSession();
        const token = sessData.session?.access_token;
        if (!token || cancelled) return;
        // fetch-SSE : JWT en en-tête Authorization, JAMAIS en query string (P0-quater ③ étendu
        // aux flux mémo/verdict ; le backend accepte déjà le header — verifyTokenFromQuery fallback).
        const url = `${BACKEND_URL}/api/public/bk/api/chat/${conversationId}/stream`;
        es = new FetchEventSource(url, { headers: { Authorization: `Bearer ${token}` } });
        const refetch = () => scheduleNext(true);
        es.addEventListener("clarity_ready", refetch);
        es.addEventListener("meeting_done", refetch);
        es.addEventListener("memo_ready", refetch);
        es.addEventListener("complete", refetch);
        es.addEventListener("verdict_ready", refetch);
        es.addEventListener("verdict", refetch);
        es.addEventListener("audit_ready", refetch);
      } catch {
        // best-effort, le polling reste actif
      }
    })();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      es?.close();
    };
  }, [id]);

  const announcedRef = useRef<string | null>(null);
  useEffect(() => {
    if (data && announcedRef.current !== data.id) {
      announcedRef.current = data.id;
      announce(`Verdict rendu : ${data.headline.slice(0, 80)}`, "assertive");
    }
  }, [data]);

  const clarityPending = Boolean(
    data && data.branch !== "audit" && data.branch !== "verif" && !data.verdictClarity && isPolling,
  );

  return { data, isLoading, error, clarityPending, clarityFailed };
}
